import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Event as EventIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";

interface SponsorshipEventDetailsModalProps {
  open: boolean;
  onClose: () => void;
  eventTitle: string;
  onEventTitleChange: (value: string) => void;
  onFlyerUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  flyerPreview: string | null;
  onSubmit: () => void;
  isSubmitting: boolean;
  primaryColor?: string;
  secondaryColor?: string;
}

const SponsorshipEventDetailsModal: React.FC<
  SponsorshipEventDetailsModalProps
> = ({
  open,
  onClose,
  eventTitle,
  onEventTitleChange,
  onFlyerUpload,
  flyerPreview,
  onSubmit,
  isSubmitting,
  primaryColor = "#1976d2",
  secondaryColor = "#f5f5f5",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: primaryColor,
          color: "white",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h4" fontWeight="bold">
            Event Details
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, mt: 1, color: "rgba(255,255,255,0.8)" }}
        >
          Share your event details for the sponsorship application
        </Typography>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {/* Event Title */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Event Title <span style={{ color: "#d32f2f" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter event title"
            value={eventTitle}
            onChange={(e) => onEventTitleChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EventIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Box>

        {/* Event Flyer Upload */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Event Flyer{" "}
            <span style={{ color: "text.secondary" }}>(Optional)</span>
          </Typography>
          <Button
            component="label"
            fullWidth
            variant="outlined"
            sx={{
              py: flyerPreview ? 1.5 : 4,
              borderRadius: 2,
              borderStyle: "dashed",
              borderWidth: 2,
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "&:hover": {
                borderColor: primaryColor,
                bgcolor: `${secondaryColor} !important`,
                borderStyle: "dashed",
                borderWidth: 2,
              },
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={onFlyerUpload}
            />

            {!flyerPreview && (
              <>
                <UploadIcon sx={{ fontSize: 32, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight="bold">
                  Click to upload event flyer
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  PNG, JPG up to 10MB
                </Typography>
              </>
            )}

            {flyerPreview && (
              <Box
                component="img"
                src={flyerPreview}
                sx={{
                  mt: { xs: 0, md: 2 },
                  maxWidth: "100%",
                  maxHeight: 200,
                  borderRadius: 1,
                  objectFit: "contain",
                }}
              />
            )}
            {flyerPreview && (
              <Typography variant="body2" mb={1}>
                Click to change flyer
              </Typography>
            )}
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={{
              py: 1.5,
              borderRadius: 2,
              color: "text.primary",
              borderColor: "divider",
              backgroundColor: secondaryColor,
              boxShadow: 0,
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onSubmit}
            disabled={!eventTitle.trim() || isSubmitting}
            sx={{
              py: 1.5,
              background: primaryColor,
              color: "white",
              boxShadow: 0,
              "&:hover": {
                background: primaryColor,
                opacity: 0.9,
              },
              "&:disabled": {
                background: "grey.300",
              },
            }}
          >
            {isSubmitting ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} sx={{ color: "white" }} />
                Submitting...
              </Box>
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SponsorshipEventDetailsModal;
